import React, { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Plus, Download, Trash2, Eye, Upload, Loader2, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader, EmptyState } from '@/components/shared'
import { companyDocumentService } from '@/services/companyDocumentService'
import { useAuthStore } from '@/store/authStore'

const uploadSchema = z.object({
  title: z.string().min(1, 'Please enter document title').max(255, 'Title must be less than 255 characters'),
  description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
})

const CompanyDocuments = () => {
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [deleteDocument, setDeleteDocument] = useState(null)
  const [viewingDocument, setViewingDocument] = useState(null)
  const [documentUrl, setDocumentUrl] = useState(null)
  const fileInputRef = useRef(null)
  
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const canUpload = user?.role === 'hr' || user?.role === 'admin'

  const form = useForm({
    resolver: zodResolver(uploadSchema),
    defaultValues: {
      title: '',
      description: '',
    },
  })

  // Fetch company documents
  const { data: documentsData, isLoading, error } = useQuery(
    ['companyDocuments'],
    () => companyDocumentService.getCompanyDocuments(),
    {
      onError: (err) => {
        toast.error('Failed to fetch company documents: ' + (err.response?.data?.error || err.message))
      }
    }
  )

  const documents = documentsData?.data?.data?.documents || []

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Check file size (10MB limit)
    if (file.size / 1024 / 1024 > 10) {
      toast.error('File must be smaller than 10MB!')
      return
    }

    // Check file type
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
      'application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Allowed types: JPG, PNG, GIF, PDF, DOC, DOCX')
      return
    }

    setSelectedFile(file)
  }

  const handleUpload = async (values) => {
    if (!selectedFile) {
      toast.error('Please select a file to upload')
      return
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('title', values.title)
      formData.append('description', values.description || '')
      formData.append('document', selectedFile)

      await companyDocumentService.uploadCompanyDocument(formData)
      
      toast.success('Document uploaded successfully')
      setIsUploadModalVisible(false)
      form.reset()
      setSelectedFile(null)
      
      queryClient.invalidateQueries(['companyDocuments'])
    } catch (error) {
      toast.error('Failed to upload document: ' + (error.response?.data?.error || error.message))
    } finally {
      setUploading(false)
    }
  }

  const handleDownload = async (doc) => {
    try {
      const response = await companyDocumentService.downloadCompanyDocument(doc.id)
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', doc.file_name)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error('Failed to download document: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleView = async (doc) => {
    try {
      const response = await companyDocumentService.downloadCompanyDocument(doc.id)
      
      const fileExtension = doc.file_name.split('.').pop().toLowerCase()
      let mimeType = 'application/octet-stream'
      
      const mimeTypes = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        txt: 'text/plain',
      }
      
      mimeType = mimeTypes[fileExtension] || mimeType
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }))
      setViewingDocument(doc)
      setDocumentUrl(url)
    } catch (error) {
      toast.error('Failed to view document: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleCloseViewModal = () => {
    setViewingDocument(null)
    if (documentUrl) {
      window.URL.revokeObjectURL(documentUrl)
      setDocumentUrl(null)
    }
  }

  const handleDelete = async () => {
    if (!deleteDocument) return
    try {
      await companyDocumentService.deleteCompanyDocument(deleteDocument.id)
      toast.success('Document deleted successfully')
      queryClient.invalidateQueries(['companyDocuments'])
    } catch (error) {
      toast.error('Failed to delete document: ' + (error.response?.data?.error || error.message))
    } finally {
      setDeleteDocument(null)
    }
  }

  const closeUploadModal = () => {
    setIsUploadModalVisible(false)
    form.reset()
    setSelectedFile(null)
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Company Documents" />
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon="folder"
              title="Failed to load company documents"
              description="There was an error loading the documents. Please try again."
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Company Documents"
        actions={
          canUpload && (
            <Button size="sm" className="h-8 px-3 text-sm" onClick={() => setIsUploadModalVisible(true)}>
              <Plus className="mr-2 h-3.5 w-3.5" />
              Upload Document
            </Button>
          )
        }
      />

      {/* Documents Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>File Name</TableHead>
              <TableHead>Uploaded By</TableHead>
              <TableHead>Upload Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No company documents found.
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium">{doc.title}</TableCell>
                  <TableCell className="text-muted-foreground">{doc.description || '-'}</TableCell>
                  <TableCell>{doc.file_name}</TableCell>
                  <TableCell>{doc.uploader?.email || 'Unknown'}</TableCell>
                  <TableCell>{dayjs(doc.uploaded_at).format('MMM DD, YYYY')}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleView(doc)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      {canUpload && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteDocument(doc)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Upload Modal */}
      <Dialog open={isUploadModalVisible} onOpenChange={closeUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Company Document</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpload)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter document title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter document description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <label className="text-sm font-medium">File</label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Select File
                  </Button>
                  {selectedFile && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-muted-foreground">
                    Supported formats: JPG, PNG, GIF, PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeUploadModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading || !selectedFile}>
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* View Document Modal */}
      <Dialog open={!!viewingDocument} onOpenChange={handleCloseViewModal}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>View Document - {viewingDocument?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {documentUrl && viewingDocument && (
              (() => {
                const fileExtension = viewingDocument.file_name.split('.').pop().toLowerCase()
                
                if (fileExtension === 'pdf') {
                  return (
                    <iframe
                      src={documentUrl}
                      className="w-full h-full min-h-[500px]"
                      title={viewingDocument.title}
                    />
                  )
                }
                
                if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)) {
                  return (
                    <div className="flex justify-center items-center h-full bg-muted/50">
                      <img
                        src={documentUrl}
                        alt={viewingDocument.title}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  )
                }
                
                return (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Document Preview Not Available</h3>
                    <p className="text-muted-foreground mb-4">
                      This file type cannot be previewed in the browser.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      File: {viewingDocument.file_name}
                    </p>
                  </div>
                )
              })()
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseViewModal}>
              Close
            </Button>
            <Button onClick={() => viewingDocument && handleDownload(viewingDocument)}>
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDocument} onOpenChange={() => setDeleteDocument(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteDocument?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default CompanyDocuments
